import ShopNavBar from '../../components/ShopNavBar'
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ItemEnquiries from '../../components/supplier/ItemEnquiries';
import { useEffect, useMemo, useState } from 'react';
import { enquiriesDbRef } from '../../utils/dbRefs';
import { equalTo, get, orderByChild, query } from 'firebase/database';
import { enquiryMarkAsRead, isNewEnquiry } from '../../utils/utils';
import ItemEnquiryShimmer from '../../components/supplier/ItemEnquiryShimmer';

const SupplierEnquiries = () => {
    const { currentUser } = useAuth();
    const [activeFilter, setActiveFilter] = useState("all");
    const [openEnquiryId, setOpenEnquiryId] = useState(null);
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEnquiries = async () => {
            if (!currentUser?.uid) return;

            setLoading(true);

            try {
                const snapshot = await get(query(enquiriesDbRef, orderByChild("shopId"), equalTo(currentUser.uid)));

                if (!snapshot.exists()) {
                    setEnquiries([]);
                    return;
                }

                const data = snapshot.val();

                const list = Object.entries(data)
                    .map(([id, value]) => ({
                        id,
                        ...value,
                    }))
                    .sort((a, b) => b.createdAt - a.createdAt);

                setEnquiries(list);
            } catch (error) {
                console.error("Error loading enquiries:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchEnquiries();
    }, [currentUser?.uid]);

    const filteredEnquiries = useMemo(() => {
        if (activeFilter === "all") return enquiries;

        if (activeFilter === "new") {
            return enquiries.filter((e) => isNewEnquiry(e.createdAt) && !e.isRead);
        }

        if (activeFilter === "read") {
            return enquiries.filter(
                (e) => !e.replied && e.isRead
            );
        }

        if (activeFilter === "replied") {
            return enquiries.filter((e) => e.replied);
        }

        return enquiries;
    }, [activeFilter, enquiries]);

    const newCount = enquiries.filter((e) => !e.isRead && isNewEnquiry(e.createdAt)).length;
    const readCount = enquiries.filter((e) => !e.replied && e.isRead).length;
    const repliedCount = enquiries.filter((e) => e.replied).length;


    const handleToggleRead = (enquiry) => {
        const isOpening = openEnquiryId !== enquiry.id;

        setOpenEnquiryId(isOpening ? enquiry.id : null);

        if (isOpening && !enquiry.isRead) {
            enquiryMarkAsRead(enquiry.id);
            setEnquiries((prev) =>
                prev.map((e) =>
                    e.id === enquiry.id ? { ...e, isRead: true } : e
                )
            );
        }
    };

    return (
        <div className='bg-background min-h-full flex flex-col font-["Plus_Jakarta_Sans",sans-serif]'>
            <ShopNavBar type={currentUser?.type || 'shop'} />
            <div className='flex-1 max-w-[1200px] mx-auto w-full px-[16px] md:px-[40px] py-[24px] md:py-[32px] pb-[80px] md:pb-[40px]'>
                <div className="mb-[24px]">
                    <h1 className="font-semibold text-[24px] md:text-[28px] text-[#212326] mb-[4px]">
                        Enquiries
                    </h1>
                    <p className="font-medium text-[14px] text-[#717182]">
                        Customer enquiries forwarded from your SignetPrint listing.
                    </p>
                </div>

                <div className="flex gap-[8px] mb-[20px] flex-wrap">

                    {/* ALL */}
                    <button
                        onClick={() => setActiveFilter("all")}
                        className={`px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] transition-all ${activeFilter === "all"
                            ? "bg-[#16a34a] text-white"
                            : "bg-white border border-[#e4e5e7] text-[#717182] hover:border-[#c0c2c6]"
                            }`}
                    >
                        All
                    </button>

                    {/* NEW */}
                    <button
                        onClick={() => setActiveFilter("new")}
                        className={`px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] transition-all ${activeFilter === "new"
                            ? "bg-[#16a34a] text-white"
                            : "bg-white border border-[#e4e5e7] text-[#717182] hover:border-[#c0c2c6]"
                            }`}
                    >
                        New
                        <span className="ml-[6px] inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-white/20 text-[10px]">
                            {newCount}
                        </span>
                    </button>

                    {/* READ */}
                    <button
                        onClick={() => setActiveFilter("read")}
                        className={`px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] transition-all ${activeFilter === "read"
                            ? "bg-[#16a34a] text-white"
                            : "bg-white border border-[#e4e5e7] text-[#717182] hover:border-[#c0c2c6]"
                            }`}
                    >
                        Read
                        <span className="ml-[6px] inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-white/20 text-[10px]">
                            {readCount}
                        </span>
                    </button>

                    {/* REPLIED */}
                    <button
                        onClick={() => setActiveFilter("replied")}
                        className={`px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] transition-all ${activeFilter === "replied"
                            ? "bg-[#16a34a] text-white"
                            : "bg-white border border-[#e4e5e7] text-[#717182] hover:border-[#c0c2c6]"
                            }`}
                    >
                        Replied
                        <span className="ml-[6px] inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-white/20 text-[10px]">
                            {repliedCount}
                        </span>
                    </button>
                </div>


                <div
                    className="flex items-start gap-[12px] bg-[#f0fdf8] border border-[#d6e0f5] rounded-[12px] px-[16px] py-[14px] mb-[20px]"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-mail w-[18px] h-[18px] text-[#16a34a] shrink-0 mt-[1px]">
                        <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                    </svg>
                    <div>
                        <p class="font-semibold text-[13px] text-[#212326] mb-[2px]">
                            Enquiries are also forwarded to your email
                        </p>
                        <p class="font-medium text-[12px] text-[#717182] leading-[1.5]">
                            Every enquiry shown here was also sent to your registered email. You can reply directly from your inbox - no dashboard action needed.
                        </p>
                    </div>
                </div>

                <div className="bg-white rounded-[16px] border border-[#e4e5e7] overflow-hidden">

                    {/* LOADING */}
                    {loading &&
                        Array.from({ length: 3 }).map((_, i) => (
                            <ItemEnquiryShimmer key={i} />
                        ))
                    }

                    {/* DATA */}
                    {!loading && filteredEnquiries.length > 0 &&
                        <div className="divide-y divide-[#f0f1f3]">
                            {filteredEnquiries.map((item) => (
                                <ItemEnquiries
                                    key={item.id}
                                    item={item}
                                    isOpen={openEnquiryId === item.id}
                                    onToggle={() => handleToggleRead(item)}
                                />
                            ))}
                        </div>
                    }

                    {/* EMPTY STATE */}
                    {!loading && filteredEnquiries.length === 0 && (
                        <div className="py-[40px] text-center">
                            <p className="text-[13px] text-[#9298a0] font-semibold">
                                No enquiries found
                            </p>
                        </div>
                    )}



                </div>

            </div>
        </div>

    )
}

export default SupplierEnquiries;
